<template>
  <section
    class="account-dashboard"
    :class="{
      desktop: $isDesktop,
      'with-next-holiday': nextBooking && !isNextBookingPast,
      'with-pending-reviews': reviewsStore.pendingReviews.length > 0,
      'is-manager': isManager,
    }"
  >
    <lazy-next-holiday
      v-if="nextBooking && !isNextBookingPast"
      :booking="nextBooking"
    />

    <AccountDashboardMessages v-if="reviewsStore.pendingReviews.length > 0" />

    <account-sections-menu />

    <recent-searches />
  </section>
  <recent-campsites
    class="recent-campsites"
    :class="{ desktop: $isDesktop }"
    :limit="$isDesktop ? 12 : 6"
    @select="recentCampsiteClickHandler()"
  />
</template>

<script lang="ts">
import AuthMixin from '~/mixins/AuthMixin'
import { Component, Vue, Watch, toNative } from '~/utility/pu-class-decorator'
import {
  NextBooking,
  NextBookingsRepositoryFetch,
} from '../../apps/user-bookings/next-booking/NextBookingsRepositoryFetch'
import { isDayPast } from '~/utility/date/relative'

@Component({
  mixins: [AuthMixin],
  setup: () => {
    return {
      appMenu: useAppMenuStore(),
      reviewsStore: useReviewsStore(),
    }
  },
})
class AccountDashboard extends Vue {
  appMenu: ReturnType<typeof useAppMenuStore>
  nextBooking: NextBooking | null = null

  async mounted() {
    await this.fetchNextBooking()
    return Promise.resolve()
  }

  @Watch('isLoggedIn')
  private async fetchNextBooking() {
    if (!this.isLoggedIn) {
      this.nextBooking = null
      return
    }
    const langCode = useI18n().i18n.locale || 'en-gb'
    const repo = new NextBookingsRepositoryFetch()
    try {
      this.nextBooking = await repo.getNext(langCode)
    } catch {
      this.nextBooking = null
    }
  }

  get isNextBookingPast() {
    return this.nextBooking ? isDayPast(this.nextBooking.arrive) : false
  }

  recentCampsiteClickHandler() {
    if (!this.$isDesktop) {
      this.appMenu.toggleAccountMenu()
    }
  }
}

export default toNative(AccountDashboard)
</script>

<style scoped>
p {
  font-size: var(--body);
}

.account-dashboard :deep(h3) {
  color: var(--colour-dark-blue);
  margin-left: 1.2em;
  font-weight: 200;
  font-size: 1.2em;
}

.account-dashboard :deep(.dashboard-messages h3) {
  margin-left: 0;
}

.card {
  background: white;
  border-radius: var(--border-radius);
  box-shadow: var(--card-box-shadow);
  color: var(--colour-dark-blue);
  user-select: none;
  padding: 1em;
  min-height: 5em;
  margin: 1.25em 1.4em 1.4em;
  display: block;
  text-decoration: none;
}

.card.next-booking {
  overflow: hidden;
  padding: 0;
  display: block;
  box-shadow: none;
}

.card span {
  font-size: var(--small);
}

.card ul {
  list-style: none;
  line-height: 1.6;
}

.card.list {
  overflow: hidden;
  overflow-x: auto;
}

.button-row {
  margin-top: 0.5em;
}

.button-row button {
  border: 1px solid var(--colour-blue);
  box-shadow: none;
  color: var(--colour-blue);
  padding: 0.4em 0.75em;
  font-size: var(--small);
  border-radius: 0.35em;
}

.menu-icon {
  width: 2em;
  height: 1.5em;
}

.menu-icon img {
  height: 100%;
  width: auto;
  vertical-align: -webkit-baseline-middle;
}

:deep(.user-updates) {
  margin: 1.4em;
}

.recent-campsites {
  margin: 0;
  overflow: visible;
}

.recent-campsites.recently-viewed-campsites {
  padding: 0 1.4em 2em;
  border-top: none;
}

.recent-campsites.recently-viewed-campsites :deep(h3) {
  font-weight: 200;
  font-size: 1.2em;
  margin: 0 0 0.75em 0;
}

.recent-campsites:not(.desktop) :deep(ul.recently-viewed) {
  font-size: unset;
  margin: 0;
}

/* desktop & tablet */
.account-dashboard.desktop {
  padding: 2em var(--page-width-padding);
  max-width: var(--page-width-max);
}

.account-dashboard.desktop.with-next-holiday,
.account-dashboard.desktop.with-pending-reviews,
.account-dashboard.desktop.is-manager {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(26em, auto));
  grid-auto-rows: auto;
  gap: 2em;
}

.account-dashboard.desktop :deep(.recents.card) {
  margin-top: 3em;
}

.account-dashboard.desktop.with-next-holiday :deep(.recents.card),
.account-dashboard.desktop.with-pending-reviews :deep(.recents.card),
.account-dashboard.desktop.is-manager :deep(.recents.card) {
  margin-top: 0;
}

.account-dashboard.desktop .card {
  margin: 0;
}

.account-dashboard.desktop .next-holiday {
  margin: 0;
}

.account-dashboard.desktop .card.updates {
  width: 100%;
}

.account-dashboard.desktop :deep(h3) {
  margin-left: 0;
}

.account-dashboard.desktop :deep(.dashboard-link) {
  display: none;
}
</style>
